import { Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils";

/**
 * The theme switch, and the sweep that reveals it.
 *
 * ── Why a View Transition and not colour transitions ──────────────────────
 * The flip used to be one `classList` call: every token in the document
 * changed in a single frame, which is the hard cut that made a whole-page
 * state change feel like a glitch rather than a decision.
 *
 * The reflex fix — `transition: background-color, color, border-color` on
 * everything — is worse than it looks. It runs a transition on every element
 * in the tree at once, and it interpolates each colour independently through
 * whatever sits between the two themes: this site's grounds go #ffffff →
 * #0a0a0a and its panels navy → near-black, so the midpoint is a wash of
 * greys that belongs to neither theme. Sixteen tokens all crossfading through
 * mud, at the cost of a full-tree style recalculation.
 *
 * A View Transition inverts that. The browser snapshots the document before
 * and after, and the only thing animated is one clip-path on the incoming
 * snapshot — so the cost is two paints and a single compositor animation, and
 * no colour is ever shown that is not one theme's real value.
 *
 * ── The sweep starts at the control ───────────────────────────────────────
 * The circle grows from the centre of the button that was clicked, so the
 * change reads as radiating out of the control rather than as something that
 * happened to the page. That is the whole idea; there is no second effect
 * layered on top. The icon swap is carried inside the same snapshot, so the
 * Sun becomes the Moon exactly as the wave passes over it.
 *
 * The toggle itself deliberately has no `view-transition-name`. It renders
 * twice at once — the desktop bar and the mobile menu both mount one — and a
 * duplicated name aborts the transition for the whole document.
 *
 * ── Where it does not run ─────────────────────────────────────────────────
 * Firefox has no View Transitions, and `prefers-reduced-motion` asks for
 * none. Both fall back to the instant swap, which is exactly what this
 * control did before, so nothing is lost where the effect cannot go.
 */

type Theme = "light" | "dark";

/** Not in TypeScript's DOM lib yet. */
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => {
    ready: Promise<void>;
    finished: Promise<void>;
  };
};

/** Long enough to read as a deliberate wipe across a full viewport, short
    enough not to feel like latency on a control you may click twice. */
const SWEEP_MS = 520;
/**
 * A moderate deceleration, not this codebase's usual `cubic-bezier(0.16,1,
 * 0.3,1)`. That curve is right for something arriving into place, but it is
 * nearly a step: 27% of the way through the duration it is already 87% done.
 * A circle compounds the problem, because the area it covers grows as the
 * square of the radius — so a front-loaded radius front-loads the coverage
 * twice over, and the back half of the sweep is an invisible crawl at the
 * edge of the screen. This keeps the wipe legible for its whole duration.
 */
const SWEEP_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem("theme");
  return stored === "dark" ? "dark" : "light";
}

/** Every DOM consequence of a theme, in one place, so the animated path and
    the instant one cannot drift apart. Idempotent by construction. */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  localStorage.setItem("theme", theme);
  const link = document.querySelector<HTMLLinkElement>("link#favicon");
  if (link) {
    link.href =
      theme === "dark" ? "/brand/favicon-32-dark.png" : "/brand/favicon-32.png";
  }
}

/** `className` lets a caller on a dark surface override the outline
    variant, which otherwise paints a light button on a dark bar. */
export default function ThemeToggle({ className }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  /* Mount, plus any path that did not already apply it imperatively. */
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  /* One flip: read the live theme and write the next one, with nothing
     awaited in between.

     Both halves have to sit in the same synchronous block. Reading at click
     time and writing later inside the transition callback looks equivalent and
     is not: a second click arriving before the first callback has run reads a
     DOM that has not changed yet, computes the same target, and silently does
     nothing. (Measured: two fast clicks from light used to end on dark.)
     Transition callbacks run serially, so doing both here means each click
     sees what the one before it actually wrote.

     React state is only ever a render-time copy of this, which is why the
     source of truth is the class on <html> and not `theme`. `theme` exists to
     render the icon and the label. */
  const flip = useCallback(() => {
    const next: Theme = document.documentElement.classList.contains("dark")
      ? "light"
      : "dark";
    /* `flushSync` commits the icon swap synchronously — without it the icon
       would pop a frame after the sweep instead of being revealed by it. */
    flushSync(() => setTheme(next));
    applyTheme(next);
  }, []);

  const toggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const doc = document as ViewTransitionDocument;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      /* Firefox has no View Transitions, and reduced motion asks for none:
         both land on the hard cut, which is what this control did before. */
      if (reduced || typeof doc.startViewTransition !== "function") {
        flip();
        return;
      }

      /* Measured before the transition starts: the button is inside the
         snapshot, so its box is only trustworthy now. */
      const box = event.currentTarget.getBoundingClientRect();
      const x = box.left + box.width / 2;
      const y = box.top + box.height / 2;
      /* Reach the farthest corner from wherever this particular toggle sits —
         top-right in the bar, mid-panel in the mobile menu. */
      const radius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y),
      );

      doc
        .startViewTransition(flip)
        .ready.then(() => {
          document.documentElement.animate(
            {
              clipPath: [
                `circle(0px at ${x}px ${y}px)`,
                `circle(${radius}px at ${x}px ${y}px)`,
              ],
            },
            {
              duration: SWEEP_MS,
              easing: SWEEP_EASE,
              pseudoElement: "::view-transition-new(root)",
            },
          );
        })
        /* A transition superseded by a second click rejects here. Its flip has
           already run, so there is nothing to recover. */
        .catch(() => {});
    },
    [flip],
  );

  return (
    <Button
      variant="outline"
      size="icon"
      className={cn(className)}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      onClick={toggle}
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}
